import { FC, useState, useEffect } from 'react';
import { Typography, FormControl, Paper, TextField, MenuItem, InputLabel, Button } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { Add as AddIcon } from '@mui/icons-material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useRecoilRefresher_UNSTABLE, useRecoilState } from 'recoil';
import { EntityDTO, OrderSettingDTO } from 'TaxAnalyticsHelpers/TaxAnalyticsClient';
import orderState, { entitiesState, currentEntityState, currentOrderMessagesState } from 'TaxAnalyticsState/orderState';
import { ApiClient } from 'TaxAnalyticsHelpers';
import moment from "moment-timezone";
import { fetchUserEntities } from 'TaxAnalyticsState/userState';
import _ from 'lodash';
import { EditEntityModal } from 'TaxAnalyticsUI/Entities/EditEntityModal';

const dateFormat = 'DD/MM/yyyy';
const dateErrorText = 'Vérifier le format de la date : jj/MM/aaaa';

export const OrderForm: FC = (props: any) => {
    const [openEntityCreationForm, setOpenEntityCreationForm] = useState<boolean>(false);
    const [entities, setEntitiesState] = useRecoilState(entitiesState);
    const [order, setOrderState] = useRecoilState(orderState);
    const [, setCurrentEntityState] = useRecoilState(currentEntityState);
    const refreshEntities = useRecoilRefresher_UNSTABLE(fetchUserEntities);
    const [orderMessages, setOrderMessages] = useRecoilState(currentOrderMessagesState);
    const currentYear = (new Date()).getFullYear();
    const years: number[] = _.range(2014, currentYear + 1);
    const handleSettingChange = (settingId: string | undefined, value?: string | Date | null) => {
        const settings: OrderSettingDTO[] = [];
        order?.orderInputSettings?.forEach(setting => {
            var newSetting = {
                id: setting.id,
                code: setting.code,
                orderId: setting.orderId,
                setting: setting.setting,
                settingId: setting.settingId,
                value: setting.value
            };
            if (settingId === setting.id) {
                newSetting.value = value?.toString() ?? "";
            }
            settings.push(newSetting);
        })
        setOrderState({ ...order, orderInputSettings: settings });
    }
    const handleEntityChange = (event: SelectChangeEvent) => {
        if (event.target.value !== "-1") {
            const entity = entities?.find(e => e.id === event.target.value)
            updateSelectedEntity(entity);
        }
        else {
            setOpenEntityCreationForm(true);
        }
    }

    const closeEntityFormCallback = (entity: EntityDTO) => {
        setOpenEntityCreationForm(false);
        updateSelectedEntity(entity);

        if (entities && entities.findIndex(e => e.id === entity.id) === -1) {
            setEntitiesState([...entities, entity]);
            refreshEntities();
        }
    }

    const updateSelectedEntity = async (entity: EntityDTO | undefined) => {
        if (entity) {
            const orderInputFiles = await ApiClient.entityClient.orderFiles(order!.id);
            setCurrentEntityState(entity);
            setOrderState({ ...order, entityId: entity.id, orderInputFiles });
        }
    };

    const isValidDate = (date: string | undefined): { error: boolean, helperText: string | undefined } => {
        var error = !moment(date, dateFormat).isValid();
        var result = {
            error: error,
            helperText: error ? dateErrorText : undefined
        };
        return result;
    }

    useEffect(() => {
        const entityErrorMessage = "Veuillez selectionner ou créer une entité pour continuer";
        const date = order?.orderInputSettings?.find(s => s.code === "VAR_DATE_EXE");
        let messages = orderMessages;
        if (date) {
            var result = isValidDate(date.value);
            if (result.error) {
                messages = [...messages, {
                    message: result.helperText!,
                    type: "error"
                }]
            }
            else {
                messages = messages.filter(m => m.message !== dateErrorText);
            }
        }
        if (!order?.entityId || order.entityId === "00000000-0000-0000-0000-000000000000") {
            messages = [...orderMessages, {
                message: entityErrorMessage,
                type: "error"
            }
            ];
        }
        else {
            messages = messages.filter(m => m.message !== entityErrorMessage);
        }
        setOrderMessages(messages);
    }, [order]);
    return (
        <Paper elevation={0}>
            <Grid container alignItems="center" justifyContent="center">
                <Grid md={6} xs={12}>
                    <Typography variant="h4">Déclarer une entité</Typography>
                </Grid>
            </Grid>
            <Grid container alignItems="center" justifyContent="center" sx={(theme) => { return { marginTop: theme.spacing(4) } }}>
                <Grid md={6} xs={12}>
                    <FormControl variant="standard" fullWidth sx={{ marginTop: 1 }}>
                        <InputLabel id="entity-select-label">Entité</InputLabel>
                        <Select
                            labelId="entity-select-label"
                            id="entity-select"
                            value={order?.entityId}
                            onChange={handleEntityChange}
                        >
                            {entities && entities.map((entity, key) => <MenuItem key={key} value={entity.id}>{entity.name}</MenuItem>)}
                            <MenuItem value="-1"><Button startIcon={<AddIcon color="primary" />}>Créer une entité</Button></MenuItem>
                        </Select>
                    </FormControl>
                    {order && order.orderInputSettings?.map((setting, index) => {
                        if (setting.setting?.settingType?.name === "Date") {
                            return (
                                <FormControl variant="standard" key={index} fullWidth sx={{ marginTop: 1 }}>
                                    <LocalizationProvider dateAdapter={AdapterMoment} key={index}>
                                        <DatePicker
                                            label={setting.setting.translations![0].name}
                                            onChange={(date) => { handleSettingChange(setting.id, date?.format(dateFormat)) }}
                                            inputFormat={dateFormat}
                                            value={(!setting.value || setting.value.length === 0 || setting.value === 'Invalid Date') ? null : moment(setting.value, dateFormat)}
                                            renderInput={(params) => <TextField id={setting.id} {...params} variant="standard" {...isValidDate(setting.value)} />}
                                        />
                                    </LocalizationProvider>
                                </FormControl>
                            )
                        }
                        if (setting.setting?.settingType?.name === "Text")
                            return (
                                <FormControl variant="standard" key={index} fullWidth sx={{ marginTop: 1 }}>
                                    <TextField
                                        id={setting.id}
                                        key={index}
                                        label={setting.setting.translations![0].name}
                                        value={setting.value}
                                        onChange={(event) => handleSettingChange(setting.id, event.target.value)}
                                        variant="standard"
                                    />
                                </FormControl>
                            )
                        if (setting.setting?.settingType?.name === "TextArea")
                            return (
                                <FormControl variant="standard" key={index} fullWidth sx={{ marginTop: 1 }}>
                                    <TextField
                                        id={setting.id}
                                        key={index}
                                        label={setting.setting.translations![0].name}
                                        value={setting.value}
                                        multiline
                                        rows={4}
                                        onChange={(event) => handleSettingChange(setting.id, event.target.value)}
                                        variant="standard"
                                    />
                                </FormControl>
                            )
                        if (setting.setting?.settingType?.name === "Number")
                            return (
                                <FormControl variant="standard" key={index} fullWidth sx={{ marginTop: 1 }}>
                                    <TextField
                                        id={setting.id}
                                        key={index}
                                        type="number"
                                        label={setting!.setting!.translations![0].name}
                                        value={setting.value}
                                        onChange={(event) => handleSettingChange(setting.id, event.target.value)}
                                        variant="standard"
                                    />
                                </FormControl>
                            )
                        if (setting.setting?.settingType?.name === "Year")
                            return (
                                <FormControl variant="standard" key={index} fullWidth sx={{ marginTop: 1 }}>
                                    <InputLabel id={setting.id}>{setting!.setting!.translations![0].name}</InputLabel>
                                    <Select
                                        id={setting.id}
                                        key={index}

                                        label={setting!.setting!.translations![0].name}
                                        value={setting.value}
                                        onChange={(event) => handleSettingChange(setting.id, event.target.value)}
                                        sx={(theme) => { return { marginTop: theme.spacing(3) } }}
                                    >
                                        {years.map((year, key) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )
                        if (setting.setting?.settingType?.name === "Select")
                            return (
                                <FormControl variant="standard" key={index} fullWidth sx={{ marginTop: 1 }}>
                                    <InputLabel id={setting.id}>{setting!.setting!.translations![0].name}</InputLabel>
                                    <Select
                                        id={setting.id}
                                        key={index}
                                        label={setting!.setting!.translations![0].name}
                                        value={setting.value}
                                        onChange={(event) => handleSettingChange(setting.id, event.target.value)}
                                        sx={(theme) => { return { marginTop: theme.spacing(3) } }}
                                    >
                                        {setting?.setting?.selectionTypeOption?.split(';').map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )
                        if (setting.setting?.settingType?.name === "Hidden")
                            return (
                                <FormControl variant="standard" key={index} fullWidth sx={{ marginTop: 1 }}>
                                    <TextField
                                        id={setting.id}
                                        key={index}
                                        value={setting.value}
                                        style={{ display: 'none' }}
                                        variant="standard"
                                    />
                                </FormControl>
                            )
                        return null;
                    })}
                </Grid>
            </Grid>
            <EditEntityModal open={openEntityCreationForm} closeCallback={closeEntityFormCallback} title="Créer une nouvelle entité" />
        </Paper >
    );
}